import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchPaymentById,
  clearSelectedEntity,
  selectSelectedPayment,
  selectPaymentDetailStatus,
  selectPaymentDetailError,
} from '../../features/payments/paymentSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import CourseItems from '../../components/reusable/CourseItems';
import StudentsCourseItems from '../../components/reusable/StudentsCourseItems';
import paymentFields from '../../config/paymentFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft } from 'react-icons/fi';

/**
 * Preview Payment Page
 * Full page read-only view of payment details
 */
const PreviewPaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedPayment = useSelector(selectSelectedPayment);
  const detailStatus = useSelector(selectPaymentDetailStatus);
  const detailError = useSelector(selectPaymentDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchPaymentById({ token: user.token, paymentId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/payments', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/payments')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  if (!selectedPayment) {
    return null;
  }

  const courseField = paymentFields.find(field => field.name === 'course_id');
  const studentField = paymentFields.find(field => field.name === 'student_id');
  const methodField = paymentFields.find(field => field.name === 'method');
  const statusField = paymentFields.find(field => field.name === 'status');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/payments"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Payment Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View payment information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/payments/${id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit Payment</span>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {courseField && (
              <motion.div variants={itemVariants}>
                <CourseItems 
                  field={courseField} 
                  formData={selectedPayment} 
                  type="preview" 
                  disabled={true} 
                />
              </motion.div>
            )}

            {studentField && (
              <motion.div variants={itemVariants}>
                <StudentsCourseItems 
                  field={studentField} 
                  formData={selectedPayment} 
                  type="preview" 
                  disabled={true} 
                />
              </motion.div>
            )}

            {methodField && (
              <motion.div variants={itemVariants}>
                <InputField
                  label={methodField.label}
                  name={methodField.name}
                  placeholder={methodField.placeholder}
                  value={methodField.options?.find((option) => option.value === selectedPayment[methodField.name])?.title || selectedPayment[methodField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}

            {statusField && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ToggleSwitch
                  label={statusField.label}
                  checked={selectedPayment[statusField.name] === 'verified' || !!selectedPayment[statusField.name]}
                  className="space-x-2"
                  disabled={true}
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewPaymentPage;
